// /src/pages/private/members/rosterTools/data/rosterData.js

import { getRankHierarchySortKey } from "../../../../../utils/rankHierarchySort";

import { BASIC_REQUIREMENT_CODES } from "../utils/trainingIcons";

/** Roster table list only — no structure cards or training details. */
export function mergeRosterListTraining(rosterData, trainings) {
  if (!rosterData?.length) return [];
  return rosterData.map((row) => {
    const list = trainings[String(row.handleId)] || [];
    const done = new Set(list.map((t) => t.code));
    return {
      ...row,
      rosterAssignment: row.rosterAssignment ?? "",
      rosterAssignmentDisplay: row.rosterAssignmentDisplay ?? "",
      division: row.division ?? "",
      branch: row.branch ?? "",
      section: row.section ?? "",
      divisionImageUrl: row.divisionImageUrl ?? "",
      branchImageUrl: row.branchImageUrl ?? "",
      sectionImageUrl: row.sectionImageUrl ?? "",
      rosterRank: row.rosterRank || row.rankName || "",
      rosterRankIcon: row.rosterRankIcon || row.rankImageUrl || null,
      basicMatrix: BASIC_REQUIREMENT_CODES.map((code) => done.has(code))
    };
  });
}

export function mergeRosterAndTraining(rosterData, trainings) {
  return rosterData.map((row) => {
    const list = trainings[String(row.handleId)] || [];

    const allTrainings = list.map((t) => ({
      code: t.code,
      label: t.label,
      level: t.trainingLevelLabel || "Unknown",
      first: t.firstParticipation,
      last: t.lastParticipation,
      count: t.participationCount
    }));

    const done = new Set(allTrainings.map((t) => t.code));

    return {
      ...row,
      rosterAssignment: row.rosterAssignment ?? "",
      rosterAssignmentDisplay: row.rosterAssignmentDisplay ?? "",
      rosterAssignmentRows: Array.isArray(row.rosterAssignmentRows)
        ? row.rosterAssignmentRows
        : [],
      division: row.division ?? "",
      branch: row.branch ?? "",
      section: row.section ?? "",
      divisionImageUrl: row.divisionImageUrl ?? "",
      branchImageUrl: row.branchImageUrl ?? "",
      sectionImageUrl: row.sectionImageUrl ?? "",
      structureCards: Array.isArray(row.structureCards) ? row.structureCards : [],
      rosterRank: row.rankName || "",
      rosterRankIcon: row.rankImageUrl || null,
      allTrainings,
      basicDetails: allTrainings.filter((t) => t.level === "Basic Training"),
      specializationDetails: allTrainings.filter(
        (t) => t.level === "Specialization"
      ),
      basicMatrix: BASIC_REQUIREMENT_CODES.map((code) => done.has(code))
    };
  });
}

function rowMatchesRosterSearch(row, lower) {
  const moniker = row.rosterMoniker?.toLowerCase() || "";
  const discord = row.rosterDiscordName?.toLowerCase() || "";
  const handle = row.rosterHandle?.toLowerCase() || "";
  const rank = row.rosterRank?.toLowerCase() || "";
  const assignment = row.rosterAssignment?.toLowerCase() || "";
  const assignmentDisplay = row.rosterAssignmentDisplay?.toLowerCase() || "";
  const division = row.division?.toLowerCase() || "";
  const branch = row.branch?.toLowerCase() || "";
  const section = row.section?.toLowerCase() || "";
  return (
    moniker.includes(lower) ||
    discord.includes(lower) ||
    handle.includes(lower) ||
    rank.includes(lower) ||
    assignment.includes(lower) ||
    assignmentDisplay.includes(lower) ||
    division.includes(lower) ||
    branch.includes(lower) ||
    section.includes(lower)
  );
}

/**
 * When searching, include every handle that shares a playerId with a matching row
 * (so a secondary match still shows the master row and siblings, and vice versa).
 */
export function filterRosterBySearch(rows, searchTerm) {
  if (!rows?.length) return [];
  const term = (searchTerm || "").trim();
  if (!term) return rows;

  const lower = term.toLowerCase();
  const matched = rows.filter((t) => rowMatchesRosterSearch(t, lower));
  if (matched.length === 0) return [];

  const playerIdToHandles = new Map();
  for (const r of rows) {
    if (r.playerId == null) continue;
    if (!playerIdToHandles.has(r.playerId)) {
      playerIdToHandles.set(r.playerId, []);
    }
    playerIdToHandles.get(r.playerId).push(r.handleId);
  }

  const includeIds = new Set();
  for (const m of matched) {
    includeIds.add(m.handleId);
    if (m.playerId != null) {
      for (const hid of playerIdToHandles.get(m.playerId) || []) {
        includeIds.add(hid);
      }
    }
  }

  return rows.filter((r) => includeIds.has(r.handleId));
}

/**
 * Order roster rows: masters and "orphan" secondaries (master absent from roster) by rank;
 * append non-master handles under their master as sub-rows (no rank on those — use rosterHandleSecondary).
 */
export function buildRosterDisplayList(rows) {
  if (!rows?.length) return [];

  const byHandleId = new Map(rows.map((r) => [r.handleId, r]));
  const playerIdToMasterHandleId = new Map();
  for (const r of rows) {
    if (r.playerId != null && r.isMaster === true) {
      playerIdToMasterHandleId.set(r.playerId, r.handleId);
    }
  }

  const isNestedSecondary = (r) => {
    if (r.isMaster !== false) return false;
    if (r.playerId == null) return false;
    const masterId = playerIdToMasterHandleId.get(r.playerId);
    if (masterId == null || masterId === r.handleId) return false;
    return byHandleId.has(masterId);
  };

  const childrenByMaster = new Map();
  const topLevel = [];

  for (const r of rows) {
    if (isNestedSecondary(r)) {
      const masterId = playerIdToMasterHandleId.get(r.playerId);
      if (!childrenByMaster.has(masterId)) {
        childrenByMaster.set(masterId, []);
      }
      childrenByMaster.get(masterId).push(r);
    } else {
      topLevel.push(r);
    }
  }

  for (const list of childrenByMaster.values()) {
    list.sort((a, b) => {
      const sa = a.rosterMoniker || a.rosterHandle || "";
      const sb = b.rosterMoniker || b.rosterHandle || "";
      return sa.localeCompare(sb, undefined, { sensitivity: "base" });
    });
  }

  topLevel.sort((a, b) => {
    const ka = getRankHierarchySortKey(a);
    const kb = getRankHierarchySortKey(b);
    if (ka === kb) return 0;
    return ka < kb ? 1 : -1;
  });

  const out = [];
  for (const r of topLevel) {
    const kids = childrenByMaster.get(r.handleId);
    const hasChildrenRoster = !!(kids && kids.length > 0);
    out.push({ ...r, rosterHandleSecondary: false, hasChildrenRoster });
    if (kids) {
      for (const k of kids) {
        out.push({ ...k, rosterHandleSecondary: true, hasChildrenRoster: false });
      }
    }
  }
  return out;
}
